// Firestore chat — same data model as the Android technician app (ChatFragment / Message / ChatInbox):
// messages live in chat/{inbox.id}/messages, ordered by `timestamp`. The parent chat doc is never written.
import { initializeApp, getApps } from "firebase/app";
import { getAuth, signInWithCustomToken } from "firebase/auth";
import {
  getFirestore, collection, query, orderBy, onSnapshot, addDoc, serverTimestamp,
} from "firebase/firestore";
import { inboxApi } from "./inboxApi";

const config = {
  // Firebase web config is public (it ships to the browser); .env can override it, defaults keep builds without .env working (G-27)
  apiKey: process.env.VUE_APP_FIREBASE_API_KEY || "AIzaSyAGhNaZH9qG1J-2xSsu5XN7LGSu_F-PGF0",
  authDomain: process.env.VUE_APP_FIREBASE_AUTH_DOMAIN || "fyxify-5d55f.firebaseapp.com",
  projectId: process.env.VUE_APP_FIREBASE_PROJECT_ID || "fyxify-5d55f",
  storageBucket: process.env.VUE_APP_FIREBASE_STORAGE_BUCKET || "fyxify-5d55f.appspot.com",
  messagingSenderId: process.env.VUE_APP_FIREBASE_MESSAGING_SENDER_ID || "1053615406636",
  appId: process.env.VUE_APP_FIREBASE_APP_ID || "1:1053615406636:web:6c9ddf25020f068e0d9b89",
};

let app = null;
function firebaseApp() {
  if (!app) app = getApps()[0] || initializeApp(config);
  return app;
}
const auth = () => getAuth(firebaseApp());
const db = () => getFirestore(firebaseApp());

// Signs in with the backend custom token (shared uid, 1h expiry) unless a Firebase session already exists.
let signingIn = null;
export async function ensureSignedIn() {
  if (auth().currentUser) return true;
  if (!signingIn) {
    signingIn = (async () => {
      const res = await inboxApi.firestoreToken();
      const token = res.ok && res.body?.firebase_auth;
      if (!token) throw new Error(res.message || "Unable to connect to chat. Please try again.");
      await signInWithCustomToken(auth(), token);
      return true;
    })().finally(() => { signingIn = null; });
  }
  return signingIn;
}

// Logged-in technician's user id (Android: user.getUser().getUser_id())
export function myUserId() {
  try {
    return parseInt(JSON.parse(localStorage.getItem("user"))?.user?.user_id, 10) || 0;
  } catch (e) {
    return 0;
  }
}

// Live listener on chat/{inboxId}/messages, newest first (Android orderBy timestamp DESCENDING).
// cb receives [{ id, ...data, date: Date|null }]; returns the unsubscribe function.
export function listen(inboxId, cb, onError) {
  const q = query(collection(db(), "chat", String(inboxId), "messages"), orderBy("timestamp", "desc"));
  return onSnapshot(q, { includeMetadataChanges: false }, (snap) => {
    cb(snap.docs.map((d) => {
      const data = d.data({ serverTimestamps: "estimate" });
      return { id: d.id, ...data, date: data.timestamp?.toDate ? data.timestamp.toDate() : null };
    }));
  }, (err) => onError && onError(err));
}

// Android ChatInbox copy stored on every message
function inboxCopy(inbox) {
  const person = (p) => ({
    id: p?.id != null ? Number(p.id) : null,
    full_name: p?.full_name ?? null,
    profile_picture: p?.profile_picture ?? null,
  });
  return {
    id: Number(inbox.id),
    status: Number(inbox.status) || 0,
    booking_id: Number(inbox.booking_id) || 0,
    message: inbox.message ?? "",
    message_time: Number(inbox.message_time) || 0,
    consumer: person(inbox.consumer),
    technician: person(inbox.technician),
  };
}

// Adds a message (same fields as Android Message + "web" platform). Returns the trimmed text, or "" if nothing was sent.
export async function send(inbox, text) {
  const message = (text || "").trim();
  if (!message) return "";
  await addDoc(collection(db(), "chat", String(inbox.id), "messages"), {
    message,
    timestamp: serverTimestamp(),
    platform: "web",
    plateform: "web",
    sender: myUserId(),
    receiver: Number(inbox.consumer?.id) || 0,
    sender_type: "technician",
    inbox: inboxCopy(inbox),
  });
  return message;
}
